import React, { useEffect, useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RecoilRoot, useRecoilValue } from "recoil";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { theme } from "styles/theme";
import {
  Experimental_CssVarsProvider as MaterialCssVarsProvider,
  THEME_ID as MATERIAL_THEME_ID,
} from "@mui/material/styles";
import { CssVarsProvider as JoyCssVarsProvider } from "@mui/joy/styles";
import { GlobalStyles } from "@mui/joy";
import { materialTheme } from "styles/materialTheme";
import "material-symbols";
import mixpanel from "mixpanel-browser";
import { BUILD_TARGET, GOOGLE_CLIENTID, isDev } from "config";
import useMixpanelWeb from "hooks/useMixpanelWeb";
import useSyncPromotionToken from "hooks/useSyncPromotionToken";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { initializeApp } from "firebase/app";
import { SendbirdWrapper } from "./sendbird/SendbirdWrapper";
import * as Sentry from "@sentry/react";
import { userAtom } from "recoil/userAtom";
import { setSentryUser } from "utils/sentry";
import ReactPixel from "react-facebook-pixel";
import { ensureFbcFromUrl, ensureFbp } from "analytics/metaCapi";
import { ChannelTalkProvider } from "contexts/ChannelTalkContext";
import { useQuery } from "@tanstack/react-query";
import {
  csInfoApi,
  CS_INFO_DOMAIN,
  CS_INFO_DETAIL_ENDPOINT,
} from "api/csInfoApi";
import { axiosInstance } from "api/instance";

// 라우팅에 필요한 모든 컴포넌트와 설정을 import 합니다.
import RootLayout from "./routes/RootLayout";
import { PATH } from "./routes/paths";
import Login from "pages/user/Login";
import Home from "pages/home/Home";
import TossPaymentsFail from "pages/tosspayments/TossPaymentsFail";
import TossPaymentsSuccess from "pages/tosspayments/TossPaymentsSuccess";
import TossPayments from "pages/tosspayments/TossPayments";
import AppleLoginRedirect from "pages/user/AppleLoginRedirect";
import KakaoLoginRedirect from "pages/KakaoLoginRedirect";
import SelfCheck from "pages/self-check/SelfCheck";
import SelfCheckEnd from "pages/self-check/SelfCheckEnd";
import MeditationTool from "pages/tool/meditation/MeditationTool";
import NewsletterSettings from "pages/newsletter/NewsletterSettings";
import OnboardingScreen from "pages/onboarding/OnboardingScreen";
import EtcWhere from "pages/onboarding/optional/EtcWhere";
import AudioGuide from "pages/tool/guide/AudioGuide";
import PromotionBook from "pages/promotion/PromotionBook";
import PromotionBookPhoneNumberSubmit from "pages/promotion/PromotionBookPhoneNumberSubmit";
import SupportPage from "./pages/mypage/SupportPage";
import SupportFaQPage from "./pages/mypage/SupportFaQPage";
import AccountPage from "./pages/mypage/AccountPage";
import AppLockScreen from "./pages/AppLockScreen";
import PCLoginPage from "./pages/login/PCLoginPage";
import LoginCoach from "./pages/user/LoginCoach";
import AppLockSetupPage from "./pages/security/AppLockSetupPage";
import RefundScreen from "./pages/refund/RefundScreen";
import TossPaymentsPromotionSuccess from "./pages/tosspayments/TossPaymentsPromotionSuccess";
import SelfCommitmentSubmitPage from "./components/commitment/SelfCommitmentSubmitPage";
import TossPaymentsPlanUpgrade from "./pages/tosspayments/TossPaymentsPlanUpgrade";
import TossPaymentsPlanUpgradeSuccess from "./pages/tosspayments/TossPaymentsPlanUpgradeSuccess";
import TossPaymentsPlanUpgradeFail from "./pages/tosspayments/TossPaymentsPlanUpgradeFail";
import PlanUpgradeScreen from "./components/task/payment/PlanUpgradeScreen";
import PrivacyPolicy from "./pages/legal-documents/PrivacyPolicy";
import TermsOfService from "./pages/legal-documents/TermsOfService";
import OnboardingScreenPromotion from "./pages/onboarding/OnboardingScreenPromotion";
import EmailLoginPage from "./pages/login/EmailLoginPage";
import LoginPromotion from "./pages/user/LoginPromotion";
import AppleLoginPromotionRedirect from "./pages/user/AppleLoginPromotionRedirect";
import PromotionCompleted from "./components/task/payment/PromotionCompleted";
import InfluencerWho from "./pages/onboarding/optional/InfluencerWho";
import RecommendWho from "./pages/onboarding/optional/RecommendWho";
import CompletedPageV3 from "./components/mkt/v3/CompletedPageV3";
import AppleLoginRedirectV3 from "./components/mkt/v3/AppleLoginRedirectV3";
import LoginPageV3 from "./components/mkt/v3/LoginPageV3";
import ProductContentPageV3 from "./components/mkt/v3/ProductContentPageV3";
import NicknameSubmitV3 from "./components/mkt/v3/NicknameSubmitV3";
import PaymentContentV3 from "./components/mkt/v3/PaymentContentV3";
import TossPaymentsSuccessV3 from "./components/mkt/v3/TossPaymentsSuccessV3";
import TossPaymentsSuccessV5 from "./components/mkt/v5/onboarding/TossPaymentsSuccessV5";
import ProductContentPageV5 from "./components/mkt/v5/onboarding/ProductContentPageV5";
import CompletedPageV5 from "./components/mkt/v5/onboarding/CompletedPageV5";
import OnboardingScreenV5 from "./components/mkt/v5/onboarding/OnboardingScreenV5";
import PromotionInitializer from "components/mkt/PromotionInitializer";
import PaymentFailPromotion from "./pages/promotion/PaymentFailPromotion";
import PromotionAlreadyUsed from "./components/task/payment/PromotionAlreadyUsed";
import KakaoLoginRedirectPromotion from "./pages/KakaoLoginRedirectPromotion";
import ProgramSetupScreen_1 from "./pages/programInit/ProgramSetupScreen_1";
import ProgramSetupScreen_2 from "./pages/programInit/ProgramSetupScreen_2";
import ProgramSetupScreen_3 from "./pages/programInit/ProgramSetupScreen_3";
import TaskEditorAccessDenied from "pages/task-editor/TaskEditorAccessDenied";
import TaskEditorPage from "pages/task-editor/TaskEditorPage";
import { canAccessTaskEditor } from "pages/task-editor/taskEditorAccess";
import TaskEditorStoreBootstrap from "components/common/TaskEditorStoreBootstrap";

export const firebaseConfig_dev = {
  apiKey: "AIzaSyAX_O-NntYzO4Bmh8M8G7PUpcP8cIwW8XI",
  authDomain: "distancing-dev.firebaseapp.com",
  projectId: "distancing-dev",
  storageBucket: "distancing-dev.appspot.com",
  messagingSenderId: "193897208164",
  appId: "1:193897208164:web:6c796023f17e225257c271",
  measurementId: "G-TXJBESPEG7",
};
export const firebaseConfig_real = {
  apiKey: "AIzaSyBMCcsN-xmAlgj6wnA1qW-S9Xlbuzdk4OI",
  authDomain: "distancing-404509.firebaseapp.com",
  projectId: "distancing-404509",
  storageBucket: "distancing-404509.appspot.com",
  messagingSenderId: "938509636319",
  appId: "1:938509636319:web:5be2e679961d04947e4f6a",
  measurementId: "G-DMCFDDT4V3",
};

const firebaseConfig = isDev ? firebaseConfig_dev : firebaseConfig_real;

function TaskEditorRouteGuard() {
  const user = useRecoilValue(userAtom);
  const canAccess = canAccessTaskEditor({
    buildTarget: BUILD_TARGET,
    hostname:
      typeof window === "undefined" ? undefined : window.location.hostname,
    user,
  });

  if (!canAccess) {
    return <TaskEditorAccessDenied />;
  }

  return (
    <>
      <TaskEditorStoreBootstrap />
      <TaskEditorPage />
    </>
  );
}

// createBrowserRouter를 사용하여 라우트 설정을 객체 형태로 정의합니다.
const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      { path: PATH.login, element: <Login /> },
      { path: PATH.emailLogin, element: <EmailLoginPage /> },
      { path: PATH.pcLogin, element: <PCLoginPage /> },
      { path: PATH.coachLogin, element: <LoginCoach /> },
      { path: PATH.applock, element: <AppLockScreen /> },
      { path: PATH.applockSetup, element: <AppLockSetupPage /> },
      { path: PATH.onboarding_new, element: <OnboardingScreen /> },
      { path: PATH.refund, element: <RefundScreen /> },
      { path: PATH.programSetup1, element: <ProgramSetupScreen_1 /> },
      { path: PATH.programSetup2, element: <ProgramSetupScreen_2 /> },
      { path: PATH.programSetup3, element: <ProgramSetupScreen_3 /> },
      { path: PATH.etc_where, element: <EtcWhere /> },
      { path: PATH.influencer_who, element: <InfluencerWho /> },
      { path: PATH.recommend_who, element: <RecommendWho /> },
      { path: PATH.home, element: <Home /> },
      { path: PATH.kakaoLoginRedirect, element: <KakaoLoginRedirect /> },
      {
        path: PATH.kakaoLoginRedirectPromotion,
        element: <KakaoLoginRedirectPromotion />,
      },
      { path: PATH.redirect_apple, element: <AppleLoginRedirect /> },
      { path: PATH.planUpgrade, element: <PlanUpgradeScreen /> },
      { path: PATH.tossPayments, element: <TossPayments /> },
      { path: PATH.privacyPolicy, element: <PrivacyPolicy /> },
      { path: PATH.termsOfService, element: <TermsOfService /> },
      {
        path: PATH.tossPaymentsPlanUpgrade,
        element: <TossPaymentsPlanUpgrade />,
      },
      { path: PATH.tossPaymentsSuccess, element: <TossPaymentsSuccess /> },
      {
        path: PATH.tossPaymentsPlanUpgradeSuccess,
        element: <TossPaymentsPlanUpgradeSuccess />,
      },
      { path: PATH.tossPaymentsFail, element: <TossPaymentsFail /> },
      {
        path: PATH.tossPaymentsPlanUpgradeFail,
        element: <TossPaymentsPlanUpgradeFail />,
      },
      { path: PATH.newsletterSettings, element: <NewsletterSettings /> },
      { path: PATH.selfCheck, element: <SelfCheck /> },
      { path: PATH.selfCheckEnd, element: <SelfCheckEnd /> },
      { path: PATH.meditationTool, element: <MeditationTool /> },
      { path: PATH.guide, element: <AudioGuide /> },
      { path: PATH.promotionBook, element: <PromotionBook /> },
      { path: PATH.account, element: <AccountPage /> },
      { path: PATH.support, element: <SupportPage /> },
      { path: PATH.supportDetail, element: <SupportFaQPage /> },
      { path: PATH.taskEditor, element: <TaskEditorRouteGuard /> },
      { path: PATH.commitment, element: <SelfCommitmentSubmitPage /> },
      {
        path: PATH.promotionPhoneNumberSubmit,
        element: <PromotionBookPhoneNumberSubmit />,
      },
      {
        path: PATH.promotionOnboarding,
        element: <OnboardingScreenPromotion />,
      },
      { path: PATH.promotionLogin, element: <LoginPromotion /> },
      {
        path: PATH.promotion_redirect_apple,
        element: <AppleLoginPromotionRedirect />,
      },
      {
        path: PATH.promotionTosspaymentsSuccess,
        element: <TossPaymentsPromotionSuccess />,
      },
      {
        path: PATH.promotionTosspaymentsFail,
        element: <PaymentFailPromotion />,
      },
      {
        path: PATH.promotionCompleted,
        element: <PromotionCompleted />,
      },
      {
        path: PATH.promotionAlreadyUsed,
        element: <PromotionAlreadyUsed />,
      },
      {
        path: PATH.v3Product,
        element: <ProductContentPageV3 />,
      },
      {
        path: PATH.v3Login,
        element: <LoginPageV3 />,
      },
      {
        path: PATH.v3_redirect_apple,
        element: <AppleLoginRedirectV3 />,
      },
      {
        path: PATH.v3NicknameSubmit,
        element: <NicknameSubmitV3 />,
      },
      { path: PATH.v3Payment, element: <PaymentContentV3 /> },
      {
        path: PATH.v3TosspaymentsSuccess,
        element: <TossPaymentsSuccessV3 />,
      },
      {
        path: PATH.v3Completed,
        element: <CompletedPageV3 />,
      },
      { path: PATH.v5Onboarding, element: <OnboardingScreenV5 /> },
      { path: PATH.v5Product, element: <ProductContentPageV5 /> },
      { path: PATH.v5TosspaymentsSuccess, element: <TossPaymentsSuccessV5 /> },
      { path: PATH.v5Completed, element: <CompletedPageV5 /> },
    ],
  },
]);

// Sentry user context를 설정하는 컴포넌트
function SentryUserSync() {
  const user = useRecoilValue(userAtom);

  useEffect(() => {
    setSentryUser(user?.userId);
  }, [user?.userId]);

  return null;
}

// ChannelTalk Provider Wrapper
function ChannelTalkWrapper({ children }: { children: React.ReactNode }) {
  const user = useRecoilValue(userAtom);

  const { data } = useQuery(
    [CS_INFO_DOMAIN, CS_INFO_DETAIL_ENDPOINT],
    () => csInfoApi.getDetail(),
    {
      enabled:
        !!axiosInstance.defaults.headers["X-Auth-Token"] &&
        !!user?.accessToken,
    }
  );

  const channelUserInfo = React.useMemo(() => {
    const info = data?.result;
    if (info?.userId && info.nickname) {
      return {
        id: String(info.userId),
        name: info.nickname,
        email: info.email,
        coachName: info.coachName,
        subscriptionStartDate: info.subscriptionStartDate,
        subscriptionPayAmount: info.subscriptionPayAmount,
        subscriptionExpirationDate: info.subscriptionExpirationDate,
      };
    }
    return null;
  }, [data?.result]);

  return (
    <ChannelTalkProvider userInfo={channelUserInfo} enabled={true}>
      {children}
    </ChannelTalkProvider>
  );
}

// axiosInstancePromotion의 토큰 동기화를 위한 컴포넌트
function PromotionTokenSync() {
  useSyncPromotionToken();
  return null;
}

function App() {
  const [queryClient] = useState(() => new QueryClient());
  const body = document.body;
  body.style.position = "fixed";
  body.style.overflow = "hidden";
  const isHome = window.location.pathname === "/";

  const isPromotionPath =
    window.location.pathname.startsWith("/promotion-") ||
    window.location.pathname.startsWith("/promotion/oauth/");

  const isV3Path =
    window.location.pathname.startsWith("/v3-") ||
    window.location.pathname.startsWith("/v3/oauth/");

  const isV5Path = window.location.pathname.startsWith("/v5-");

  const setMixPanel_come = useMixpanelWeb({
    eventName: "진입",
  });

  useEffect(() => {
    if (isHome) {
      setMixPanel_come();
    }
  }, [isHome, setMixPanel_come]);

  const googleClientid = GOOGLE_CLIENTID;

  const app = initializeApp(firebaseConfig);

  // Initialize Google Tag (gtag) only in Real environment
  useEffect(() => {
    if (BUILD_TARGET === "real") {
      // 1. 중복 실행 방지 (이미 스크립트가 있으면 종료)
      if (document.getElementById("google-gtag-script")) return;

      // 2. dataLayer 및 gtag 함수 즉시 초기화
      // (스크립트 로딩을 기다리지 않고 명령을 미리 쌓아둡니다 -> 데이터 유실 방지)
      window.dataLayer = window.dataLayer || [];
      window.gtag = function () {
        window.dataLayer.push(arguments);
      };

      // 3. 초기 설정 즉시 실행
      window.gtag("js", new Date());
      window.gtag("config", "AW-10862597581");

      // 4. 스크립트 비동기 로드
      const script = document.createElement("script");
      script.id = "google-gtag-script"; // ID 부여
      script.src = "https://www.googletagmanager.com/gtag/js?id=AW-10862597581";
      script.async = true;
      document.head.appendChild(script);

      console.log("[gtag] Initialized in Real environment");
    }
  }, []);

  if (isPromotionPath) {
    const marketingProjectId = isDev
      ? "671725f1abeed2fd78dbf21de4739bcd"
      : "89f46b9084531b79079079d885af942f";
    const FB_PIXEL_ID = isDev ? "864462499876586" : "891312099992669";

    ensureFbcFromUrl();
    ensureFbp();

    ReactPixel.init(FB_PIXEL_ID, undefined, {
      autoConfig: false, // 자동 설정
      debug: isDev, // 디버그 모드
    });
    // Trigger PageView to ensure fbp cookie is created
    ReactPixel.pageView();
    if (typeof window !== "undefined" && (window as any).fbq) {
      (window as any).fbq.disablePushState = true;
    }

    mixpanel.init(
      marketingProjectId,
      {
        debug: isDev,
        api_method: "POST",
        api_transport: "XHR",
        batch_requests: false,
        ignore_dnt: false,
        disable_persistence: false,
        persistence: "cookie",
        cookie_domain: "",
        cookie_expiration: 365,
        cross_site_cookie: false,
        cross_subdomain_cookie: true,
        ip: true,
        opt_out_tracking_by_default: false,
        opt_out_persistence_by_default: false,
        opt_out_tracking_persistence_type: "localStorage",
        opt_out_tracking_cookie_prefix: undefined,
        persistence_name: "",
        property_blacklist: [],
        secure_cookie: false,
        track_links_timeout: 300,
        upgrade: false,
        xhr_headers: {},
      },
      "promotion"
    );

    console.log("[Mixpanel] Initialized promotion project only");
  } else if (isV3Path) {
    const FB_PIXEL_ID = isDev ? "2278923959284145" : "3631174720354947";

    ensureFbcFromUrl();
    ensureFbp();

    ReactPixel.init(FB_PIXEL_ID, undefined, {
      autoConfig: false, // 자동 설정
      debug: isDev, // 디버그 모드
    });

    mixpanel.init(
      isDev
        ? "5afefbf30aa92453db009d8192c0d5aa"
        : "68673de9873f1a703ff1dfa69408551e",
      {
        debug: isDev,
        api_method: "POST",
        api_transport: "XHR",
        batch_requests: false,
        ignore_dnt: false,
        disable_persistence: false,
        persistence: "cookie",
        cookie_domain: "",
        cookie_expiration: 365,
        cross_site_cookie: false,
        cross_subdomain_cookie: true,
        ip: true,
        opt_out_tracking_by_default: false,
        opt_out_persistence_by_default: false,
        opt_out_tracking_persistence_type: "localStorage",
        opt_out_tracking_cookie_prefix: undefined,
        persistence_name: "",
        property_blacklist: [],
        secure_cookie: false,
        track_links_timeout: 300,
        upgrade: false,
        xhr_headers: {},
      },
      "v3"
    );
  } else if (isV5Path) {
    const FB_PIXEL_ID = isDev ? "1147995483797146" : "1164825235374922";

    ensureFbcFromUrl();
    ensureFbp();

    ReactPixel.init(FB_PIXEL_ID, undefined, {
      autoConfig: false, // 자동 설정
      debug: isDev, // 디버그 모드
    });

    mixpanel.init(
      isDev
        ? "7696d7fd464f1600009378bbe2e5bb5e"
        : "74a431925b99772bb5e533af27833e6f",
      {
        debug: isDev,
        api_method: "POST",
        api_transport: "XHR",
        batch_requests: false,
        ignore_dnt: false,
        disable_persistence: false,
        persistence: "cookie",
        cookie_domain: "",
        cookie_expiration: 365,
        cross_site_cookie: false,
        cross_subdomain_cookie: true,
        ip: true,
        opt_out_tracking_by_default: false,
        opt_out_persistence_by_default: false,
        opt_out_tracking_persistence_type: "localStorage",
        opt_out_tracking_cookie_prefix: undefined,
        persistence_name: "",
        property_blacklist: [],
        secure_cookie: false,
        track_links_timeout: 300,
        upgrade: false,
        xhr_headers: {},
      },
      "v5"
    );
  } else {
    const FB_PIXEL_ID = isDev ? "864462499876586" : "891312099992669";

    ensureFbp();

    ReactPixel.init(FB_PIXEL_ID, undefined, {
      autoConfig: false, // 자동 설정
      debug: isDev, // 디버그 모드
    });

    const mainProjectId = isDev
      ? "306a1663b1fece6e28324733a9aca2ee"
      : "6bb06d65a8dec1d9dec0496adef7c8d0";

    mixpanel.init(mainProjectId, {
      debug: isDev,
      api_method: "POST",
      api_transport: "XHR",
      batch_requests: false,
      batch_size: 50,
      batch_flush_interval_ms: 5000,
      batch_request_timeout_ms: 90000,
      record_sessions_percent: 0, // 수동 제어
      record_heatmap_data: true,
      cookie_domain: "",
      cookie_expiration: 365,
      cross_site_cookie: false,
      cross_subdomain_cookie: true,
      disable_persistence: false,
      ip: true,
      opt_out_tracking_by_default: false,
      opt_out_persistence_by_default: false,
      opt_out_tracking_persistence_type: "localStorage",
      opt_out_tracking_cookie_prefix: undefined,
      persistence: "cookie",
      persistence_name: "",
      property_blacklist: [],
      secure_cookie: false,
      track_links_timeout: 300,
      upgrade: false,
      xhr_headers: {},
      ignore_dnt: false,
    });
  }

  return (
    <GoogleOAuthProvider clientId={googleClientid}>
      <MaterialCssVarsProvider
        defaultMode="system"
        theme={{ [MATERIAL_THEME_ID]: materialTheme }}
      >
        <JoyCssVarsProvider defaultMode="system" theme={theme}>
          <GlobalStyles
            styles={{
              html: {
                boxSizing: "border-box",
              },
              body: {},
            }}
          />
          <RecoilRoot>
            <Sentry.ErrorBoundary
              fallback={
                isDev
                  ? ({ error, resetError }) => (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          height: "100vh",
                          padding: "20px",
                        }}
                      >
                        <h2>문제가 발생했습니다</h2>
                        <p>잠시 후 다시 시도해주세요.</p>
                        <p>{error.message}</p>
                        <button
                          onClick={resetError}
                          style={{
                            marginTop: "20px",
                            padding: "10px 20px",
                            cursor: "pointer",
                          }}
                        >
                          다시 시도
                        </button>
                      </div>
                    )
                  : undefined
              }
            >
              <SentryUserSync />
              <PromotionTokenSync />
              {isPromotionPath && <PromotionInitializer />}
              <QueryClientProvider client={queryClient}>
                <ChannelTalkWrapper>
                  <SendbirdWrapper>
                    <RouterProvider router={router} />
                  </SendbirdWrapper>
                </ChannelTalkWrapper>
              </QueryClientProvider>
            </Sentry.ErrorBoundary>
          </RecoilRoot>
        </JoyCssVarsProvider>
      </MaterialCssVarsProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
